import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { Category, Product } from '@/types'

import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { formatIDR } from '@/lib/format'
import { buildProductMessage } from '@/lib/whatsapp'
import { getCategories, getProductBySlug, getProductSlugs, getProducts } from '@/lib/catalog'
import { useShop } from '@/components/providers/shop-provider'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { ProductCard } from '@/components/shop/product-card'
import { ProductMedia } from '@/components/shop/product-media'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'

import {
    ChevronRight,
    HandHeart,
    MessageCircleHeart,
    Ruler,
    ShoppingBag,
    Sparkles
} from 'lucide-react'

import {
    ColorSwatches,
    QuantitySelector,
    StockBadge,
    WishlistButton
} from '@/components/shop/shop-bits'

interface ProductPageProps {
    product: Product
    related: Array<Product>
    categories: Array<Category>
}

const CARE = 'Spot clean gently with a damp cloth and mild soap, then let your doll air dry. Keep away from open flames and hot radiators, and supervise very young children.'

const ProductPage: FC<ProductPageProps> = ({ product, related, categories }): ReactNode => {
    const { addToCart } = useShop()
    const [colorIndex, setColorIndex] = useState(0)
    const [quantity, setQuantity] = useState(1)
    const [note, setNote] = useState('')

    useEffect(() => {
        setColorIndex(0)
        setQuantity(1)
        setNote('')
    }, [product.id])

    const color = product.colors[Math.min(colorIndex, Math.max(0, product.colors.length - 1))] ?? null
    const soldOut = !product.madeToOrder && product.stock <= 0
    const maxQuantity = product.madeToOrder ? null : product.stock

    const message = useCallback((origin: string) => buildProductMessage({
        name: product.name,
        slug: product.slug,
        price: product.price,
        quantity,
        colorLabel: color?.label ?? null,
        madeToOrder: product.madeToOrder,
        note,
        origin
    }), [product, quantity, color, note])

    const handleAdd = () => {
        addToCart({
            productId: product.id,
            slug: product.slug,
            name: product.name,
            price: product.price,
            image: product.image,
            designKey: product.designKey,
            colorLabel: color?.label ?? null,
            colorHex: color?.hex ?? null,
            accentColor: product.accentColor,
            maxQuantity,
            quantity
        })
    }

    const seoDescription = product.tagline
        ? `${product.tagline} Handmade crochet doll, ${formatIDR(product.price)}. Order easily on WhatsApp.`
        : `Handmade crochet doll by Crochet World, ${formatIDR(product.price)}. Order easily on WhatsApp.`

    return (
        <Layout categories={categories}>
            <Seo title={product.name} description={seoDescription} ogImage={product.image ?? undefined} />

            <div className='mx-auto max-w-7xl px-5 pb-24 pt-8 sm:px-8 sm:pt-12'>
                <nav aria-label='Breadcrumb' className='text-sm text-muted-foreground'>
                    <ol className='flex flex-wrap items-center gap-1.5'>
                        <li>
                            <Link href='/shop' className='hover:text-cocoa-900'>
                                Shop
                            </Link>
                        </li>
                        {product.category && (
                            <>
                                <li aria-hidden='true'>
                                    <ChevronRight className='size-3.5' />
                                </li>
                                <li>
                                    <Link href={`/shop?category=${product.category.slug}`} className='hover:text-cocoa-900'>
                                        <span data-mdlfy-cms={`categories:${product.category.id}:name`}>
                                            {product.category.name}
                                        </span>
                                    </Link>
                                </li>
                            </>
                        )}
                        <li aria-hidden='true'>
                            <ChevronRight className='size-3.5' />
                        </li>
                        <li aria-current='page' className='font-semibold text-cocoa-800'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>
                                {product.name}
                            </span>
                        </li>
                    </ol>
                </nav>

                <div className='mt-8 grid gap-12 lg:grid-cols-[1.1fr_1fr] lg:gap-16'>
                    <div className='lg:sticky lg:top-28 lg:self-start'>
                        <ProductMedia product={product} colorHex={color?.hex ?? null} />
                    </div>

                    <div>
                        {product.category && (
                            <Link
                                href={`/shop?category=${product.category.slug}`}
                                className='inline-flex rounded-full bg-blush-100 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-blush-600 transition hover:bg-blush-200'
                            >
                                <span data-mdlfy-cms={`categories:${product.category.id}:name`}>
                                    {product.category.name}
                                </span>
                            </Link>
                        )}

                        <h1 className='mt-4 font-heading text-5xl leading-[1.02] tracking-[-0.02em] text-cocoa-900 font-soft sm:text-6xl'>
                            <span data-mdlfy-cms={`products:${product.id}:name`}>
                                {product.name}
                            </span>
                        </h1>

                        {product.tagline && (
                            <p className='mt-4 text-lg leading-relaxed text-muted-foreground'>
                                <span data-mdlfy-cms={`products:${product.id}:tagline`}>
                                    {product.tagline}
                                </span>
                            </p>
                        )}

                        <div className='mt-6 flex flex-wrap items-center gap-x-6 gap-y-3'>
                            <p className='font-heading text-4xl text-cocoa-900'>
                                {formatIDR(product.price)}
                            </p>
                            <StockBadge stock={product.stock} madeToOrder={product.madeToOrder} />
                            {product.badge && (
                                <span className='rounded-full border border-cocoa-700/15 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-cocoa-700'>
                                    <span data-mdlfy-cms={`products:${product.id}:badge`}>
                                        {product.badge}
                                    </span>
                                </span>
                            )}
                        </div>

                        <div className='mt-8 space-y-7 rounded-[32px] border border-border bg-white/70 p-6 sm:p-7'>
                            {product.colors.length > 0 && (
                                <ColorSwatches colors={product.colors} value={colorIndex} onChange={setColorIndex} label='Available colours' />
                            )}

                            {product.dimensions && (
                                <p className='flex items-center gap-2.5 text-sm text-cocoa-700'>
                                    <Ruler className='size-4 text-cocoa-500' />
                                    <span className='text-muted-foreground'>Size</span>
                                    <span className='font-semibold'>
                                        <span data-mdlfy-cms={`products:${product.id}:dimensions`}>
                                            {product.dimensions}
                                        </span>
                                    </span>
                                </p>
                            )}

                            <div>
                                <p className='mb-2.5 text-sm text-muted-foreground'>
                                    Quantity
                                </p>
                                <div className='flex flex-wrap items-center gap-3'>
                                    <QuantitySelector value={quantity} max={maxQuantity} onChange={setQuantity} />
                                    <button
                                        type='button'
                                        onClick={handleAdd}
                                        disabled={soldOut}
                                        className='inline-flex h-12 flex-1 items-center justify-center gap-2 rounded-full bg-cocoa-800 px-6 text-sm font-semibold text-cream-50 shadow-[0_16px_36px_-18px_rgba(58,40,30,0.8)] transition hover:-translate-y-0.5 hover:bg-cocoa-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 sm:min-w-[200px]'
                                    >
                                        <ShoppingBag className='size-5' />
                                        {soldOut ? 'Sold out' : product.madeToOrder ? 'Add request to cart' : 'Add to cart'}
                                    </button>
                                    <WishlistButton
                                        variant='pill'
                                        item={{
                                            productId: product.id,
                                            slug: product.slug,
                                            name: product.name,
                                            price: product.price,
                                            image: product.image,
                                            designKey: product.designKey,
                                            colorHex: product.colors[0]?.hex ?? null,
                                            accentColor: product.accentColor
                                        }}
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor='maker-note' className='text-sm font-semibold text-cocoa-800'>
                                    {product.madeToOrder ? 'Tell us about your idea' : 'Note for the maker'}
                                    <span className='font-normal text-muted-foreground'> (optional)</span>
                                </label>
                                <textarea
                                    id='maker-note'
                                    value={note}
                                    onChange={event => setNote(event.target.value)}
                                    rows={product.madeToOrder ? 3 : 2}
                                    maxLength={600}
                                    placeholder={product.madeToOrder ? 'Who should it look like? Colours, outfit, a name to embroider...' : 'Gift wrap, a birthday message, delivery city...'}
                                    className='mt-2 w-full resize-none rounded-2xl border border-input bg-white px-4 py-3 text-sm outline-none transition focus:border-caramel-400 focus:ring-2 focus:ring-caramel-300/40'
                                />
                                <WhatsAppLink message={message} size='lg' className='mt-3 w-full'>
                                    {product.madeToOrder ? 'Request via WhatsApp' : 'Order via WhatsApp'}
                                </WhatsAppLink>
                                <p className='mt-2 text-center text-xs text-muted-foreground'>
                                    Opens WhatsApp with this doll, colour, and quantity filled in.
                                </p>
                            </div>
                        </div>

                        <ul className='mt-8 grid gap-3 sm:grid-cols-3'>
                            {[
                                { icon: HandHeart, title: 'Handmade', text: 'Crocheted stitch by stitch' },
                                { icon: Sparkles, title: 'One of a kind', text: 'Tiny quirks make it yours' },
                                { icon: MessageCircleHeart, title: 'Easy ordering', text: 'Confirm details on WhatsApp' }
                            ].map(item => {
                                const Icon = item.icon

                                return (
                                    <li key={item.title} className='rounded-3xl bg-sage-50 p-4'>
                                        <Icon className='size-5 text-sage-700' />
                                        <p className='mt-2 text-sm font-bold text-cocoa-800'>
                                            {item.title}
                                        </p>
                                        <p className='text-xs text-muted-foreground'>
                                            {item.text}
                                        </p>
                                    </li>
                                )
                            })}
                        </ul>

                        <div className='mt-10 divide-y divide-border border-y border-border'>
                            {product.description && (
                                <details open className='group py-5'>
                                    <summary className='flex cursor-pointer list-none items-center justify-between font-heading text-xl text-cocoa-900'>
                                        About this doll
                                        <ChevronRight className='size-5 text-cocoa-500 transition-transform group-open:rotate-90' />
                                    </summary>
                                    <p className='mt-4 whitespace-pre-line text-[15px] leading-relaxed text-cocoa-700'>
                                        <span data-mdlfy-cms={`products:${product.id}:description`}>
                                            {product.description}
                                        </span>
                                    </p>
                                </details>
                            )}
                            <details className='group py-5'>
                                <summary className='flex cursor-pointer list-none items-center justify-between font-heading text-xl text-cocoa-900'>
                                    Materials and size
                                    <ChevronRight className='size-5 text-cocoa-500 transition-transform group-open:rotate-90' />
                                </summary>
                                <dl className='mt-4 grid gap-3 text-[15px] text-cocoa-700 sm:grid-cols-[140px_1fr]'>
                                    {product.materials && (
                                        <>
                                            <dt className='font-semibold text-cocoa-800'>Materials</dt>
                                            <dd>
                                                <span data-mdlfy-cms={`products:${product.id}:materials`}>
                                                    {product.materials}
                                                </span>
                                            </dd>
                                        </>
                                    )}
                                    {product.dimensions && (
                                        <>
                                            <dt className='font-semibold text-cocoa-800'>Size</dt>
                                            <dd>
                                                <span data-mdlfy-cms={`products:${product.id}:dimensions`}>
                                                    {product.dimensions}
                                                </span>
                                            </dd>
                                        </>
                                    )}
                                    <dt className='font-semibold text-cocoa-800'>Colours</dt>
                                    <dd>
                                        {product.colors.length ? product.colors.map(entry => entry.label).join(', ') : 'Ask us for your favourite colours'}
                                    </dd>
                                </dl>
                            </details>
                            <details className='group py-5'>
                                <summary className='flex cursor-pointer list-none items-center justify-between font-heading text-xl text-cocoa-900'>
                                    Caring for your doll
                                    <ChevronRight className='size-5 text-cocoa-500 transition-transform group-open:rotate-90' />
                                </summary>
                                <p className='mt-4 text-[15px] leading-relaxed text-cocoa-700'>
                                    {CARE}
                                </p>
                            </details>
                        </div>
                    </div>
                </div>

                {related.length > 0 && (
                    <section aria-labelledby='related-title' className='mt-24'>
                        <div className='flex items-end justify-between gap-4'>
                            <h2 id='related-title' className='font-heading text-4xl text-cocoa-900 font-soft'>
                                You may also love
                            </h2>
                            <Link href='/shop' className='hidden text-sm font-semibold text-cocoa-700 underline-offset-4 hover:underline sm:block'>
                                Shop all dolls
                            </Link>
                        </div>
                        <div className={cn('mt-10 grid grid-cols-2 gap-x-4 gap-y-10 sm:gap-x-6 lg:grid-cols-4')}>
                            {related.map(item => (
                                <ProductCard key={item.id} product={item} />
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => {
    try {
        const slugs = await getProductSlugs()

        return { paths: slugs.map(slug => ({ params: { slug } })), fallback: 'blocking' }
    }
    catch (error) {
        console.error('Could not list product pages:', error instanceof Error ? error.message : error)

        return { paths: [], fallback: 'blocking' }
    }
}

export const getStaticProps: GetStaticProps<ProductPageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    try {
        const product = await getProductBySlug(slug)

        if (!product) return { notFound: true, revalidate: 30 }

        const [products, categories] = await Promise.all([getProducts(), getCategories()])
        const sameCategory = products.filter(item => item.id !== product.id && item.category?.id === product.category?.id)
        const others = products.filter(item => item.id !== product.id && item.category?.id !== product.category?.id && item.isFeatured)
        const related = [...sameCategory, ...others].slice(0, 4)

        return { props: { product, related, categories }, revalidate: 60 }
    }
    catch (error) {
        console.error('Could not load product page:', error instanceof Error ? error.message : error)

        return { notFound: true, revalidate: 10 }
    }
}

export default ProductPage
